// DetailPanel — slides in from the right when a card is selected
// Shows AI summary, documents, suggested drafts, open questions,
// people, related items, and zone appearances

import React, { useMemo, useState } from 'react';
import { UnitData, PresenceShape, CognitiveZone } from './GlowingPresence';

// ============================================================
// Clean light palette — bold, saturated, no pastels
// ============================================================

const C = {
  // Backgrounds
  bg:         '#FFFFFF',
  surface:    '#F6F7F9',
  surfaceHov: '#EDEEF2',
  border:     '#D4D7DE',
  borderLight:'#E3E5EB',

  // Text — high contrast
  text:       '#111318',
  textSec:    '#4A4F5C',
  textMuted:  '#7A7F8E',
  textFaint:  '#A3A8B4',

  // Accents — strong, not pastel
  blue:       '#2563EB',  // vivid blue
  blueBg:     '#EFF4FF',
  red:        '#DC2626',  // true red
  redBg:      '#FEF2F2',
  green:      '#16A34A',  // rich green
  greenBg:    '#F0FDF4',
  amber:      '#D97706',  // deep amber
  amberBg:    '#FFFBEB',
  purple:     '#7C3AED',  // vivid purple
  purpleBg:   '#F5F3FF',
  teal:       '#0D9488',  // deep teal
  tealBg:     '#F0FDFA',
};

// ============================================================
// Types
// ============================================================

interface ZoneAppearance {
  zone: CognitiveZone;
  facetLabel: string;
}

interface EntityDocument {
  id: string;
  name: string;
  type: 'pdf' | 'docx' | 'xlsx' | 'email' | 'link' | 'note';
  addedBy: string;
  date: string;
  size?: string;
}

interface SuggestedDraft {
  id: string;
  type: 'email' | 'document' | 'note';
  title: string;
  recipient?: string;
  reason: string;
  preview: string;
}

interface OpenQuestion {
  id: string;
  question: string;
  priority: 'high' | 'medium' | 'low';
  source: string;
}

interface AIInsight {
  summary: string;
  riskFactors: string[];
  nextSteps: string[];
  documents: EntityDocument[];
  suggestedDrafts: SuggestedDraft[];
  openQuestions: OpenQuestion[];
}

export interface DetailPanelProps {
  unit: UnitData | null;
  zoneAppearances: ZoneAppearance[];
  onClose: () => void;
  onAction?: (actionId: string, unitId: string) => void;
}

// ============================================================
// Helpers
// ============================================================

const ZONE_DISPLAY: Record<CognitiveZone, { label: string; color: string; bg: string; icon: string }> = {
  [CognitiveZone.FOCUSED_EXECUTION]:     { label: 'Working On',       color: C.teal,   bg: C.tealBg,   icon: '🎯' },
  [CognitiveZone.REACTIVE_TRIAGE]:       { label: 'Needs Attention',  color: C.red,    bg: C.redBg,    icon: '⚡' },
  [CognitiveZone.REFLECTIVE_THINKING]:   { label: 'Thinking About',   color: C.blue,   bg: C.blueBg,   icon: '💡' },
  [CognitiveZone.COLLABORATIVE_WAITING]: { label: 'Waiting On',       color: C.purple, bg: C.purpleBg, icon: '⏳' },
};

function getTypeIcon(type: PresenceShape): string {
  switch (type) {
    case PresenceShape.PERSON: return '👤';
    case PresenceShape.DEAL: return '💰';
    case PresenceShape.DOCUMENT: return '📄';
    case PresenceShape.PROJECT: return '📋';
    default: return '•';
  }
}

function getTypeLabel(type: PresenceShape): string {
  switch (type) {
    case PresenceShape.PERSON: return 'Person';
    case PresenceShape.DEAL: return 'Deal';
    case PresenceShape.DOCUMENT: return 'Document';
    case PresenceShape.PROJECT: return 'Project';
    default: return 'Item';
  }
}

function docIcon(type: EntityDocument['type']): string {
  switch (type) {
    case 'pdf': return '📕';
    case 'docx': return '📘';
    case 'xlsx': return '📗';
    case 'email': return '📧';
    case 'link': return '🔗';
    case 'note': return '📝';
    default: return '📄';
  }
}

function healthLabel(h: number): { text: string; color: string } {
  if (h >= 0.8) return { text: 'Healthy', color: C.green };
  if (h >= 0.5) return { text: 'At Risk', color: C.amber };
  if (h >= 0.25) return { text: 'Critical', color: C.red };
  return { text: 'Failing', color: '#B91C1C' };
}

const priorityStyle: Record<string, { color: string; bg: string }> = {
  high:   { color: C.red,       bg: C.redBg },
  medium: { color: C.amber,     bg: C.amberBg },
  low:    { color: C.textMuted, bg: C.surface },
};

// ============================================================
// AI Insights per entity (demo data)
// ============================================================

const AI_INSIGHTS: Record<string, AIInsight> = {
  morrison: {
    summary: 'Morrison Manufacturing is in active negotiation with a strong champion (Patricia Simmons), but has critical gaps: the CISO is not engaged and the CFO has gone silent — a pattern that preceded 3 lost deals last quarter. Proposal v3 is with the client but pricing needs executive buy-in.',
    riskFactors: [
      'CFO silence matches pattern from lost Meridian and Apex deals',
      'CISO engagement required for Stage 2 security review — not started',
      'Competitor Forge Systems presented to their team 2 weeks ago',
    ],
    nextSteps: [
      'Get Patricia to arrange a CFO introduction this week',
      'Send CISO-specific security whitepaper',
      'Follow up on Proposal v3 pricing feedback',
    ],
    documents: [
      { id: 'd1', name: 'Morrison Proposal v3.docx', type: 'docx', addedBy: 'You', date: '3 days ago', size: '2.4 MB' },
      { id: 'd2', name: 'Security Compliance Overview.pdf', type: 'pdf', addedBy: 'Sam (AI)', date: '1 week ago', size: '890 KB' },
      { id: 'd3', name: 'Morrison - Meeting Notes Feb 12.docx', type: 'docx', addedBy: 'You', date: '1 week ago', size: '45 KB' },
      { id: 'd4', name: 'Competitor Analysis - Forge Systems.xlsx', type: 'xlsx', addedBy: 'Maya (AI)', date: '2 weeks ago', size: '156 KB' },
      { id: 'd5', name: 'Patricia Simmons - LinkedIn Profile', type: 'link', addedBy: 'Maya (AI)', date: '3 weeks ago' },
    ],
    suggestedDrafts: [
      {
        id: 's1', type: 'email', title: 'CISO Introduction Request',
        recipient: 'Patricia Simmons',
        reason: 'CISO engagement is required for Stage 2 and hasn\'t been initiated',
        preview: 'Hi Patricia, I wanted to discuss the security review process with your CISO, Mike Thompson. Given our timeline for Stage 2...',
      },
      {
        id: 's2', type: 'email', title: 'CFO Check-in via Patricia',
        recipient: 'Patricia Simmons',
        reason: 'CFO has been silent — this pattern preceded 3 lost deals',
        preview: 'Patricia, I noticed we haven\'t heard from Frank regarding the pricing structure. Would it help if I prepared a brief executive summary...',
      },
      {
        id: 's3', type: 'document', title: 'CISO Security Brief',
        reason: 'Tailored security document for Mike Thompson based on Morrison\'s compliance requirements',
        preview: 'A concise 2-page overview of our SOC 2 compliance, data residency options, and enterprise SSO capabilities...',
      },
    ],
    openQuestions: [
      { id: 'q1', question: 'What is Morrison\'s budget approval process and who has final sign-off?', priority: 'high', source: 'Deal stage analysis' },
      { id: 'q2', question: 'Has the CISO reviewed any of our security documentation?', priority: 'high', source: 'Stakeholder gap' },
      { id: 'q3', question: 'What is the competitive evaluation timeline?', priority: 'medium', source: 'Competitor activity detected' },
      { id: 'q4', question: 'Are there integration requirements with their existing ERP system?', priority: 'medium', source: 'Technical discovery incomplete' },
    ],
  },
  heartland: {
    summary: 'Heartland Foods is a healthy upsell opportunity. Tom Bradley is engaged and the expansion proposal has been sent. Activity is moderate — needs a follow-up to keep momentum.',
    riskFactors: [
      'No response to expansion proposal in 5 days',
    ],
    nextSteps: [
      'Follow up with Tom on expansion proposal',
      'Prepare ROI analysis for the expanded tier',
    ],
    documents: [
      { id: 'd1', name: 'Heartland Expansion Proposal.docx', type: 'docx', addedBy: 'You', date: '5 days ago', size: '1.8 MB' },
      { id: 'd2', name: 'Current Usage Analytics.xlsx', type: 'xlsx', addedBy: 'Maya (AI)', date: '1 week ago', size: '340 KB' },
    ],
    suggestedDrafts: [
      {
        id: 's1', type: 'email', title: 'Expansion Proposal Follow-up',
        recipient: 'Tom Bradley',
        reason: 'No response to proposal sent 5 days ago',
        preview: 'Hi Tom, wanted to check in on the expansion proposal I sent over last week. Happy to walk through the pricing structure...',
      },
    ],
    openQuestions: [
      { id: 'q1', question: 'What is Heartland\'s renewal date for their current tier?', priority: 'medium', source: 'Upsell timing' },
      { id: 'q2', question: 'Are there additional departments interested in the platform?', priority: 'low', source: 'Expansion opportunity' },
    ],
  },
  enterprise: {
    summary: 'Enterprise Tier Launch is on track for March 1. Kevin is working on final pricing. The project plan and launch playbook are in good shape. Main risk is pricing finalization before the launch date.',
    riskFactors: [
      'Pricing not finalized with 10 days to launch',
    ],
    nextSteps: [
      'Finalize pricing model with Kevin by Friday',
      'Complete launch playbook review',
      'Schedule go/no-go meeting for Feb 25',
    ],
    documents: [
      { id: 'd1', name: 'Enterprise Tier - Project Plan.xlsx', type: 'xlsx', addedBy: 'You', date: '2 days ago', size: '560 KB' },
      { id: 'd2', name: 'Launch Playbook v2.docx', type: 'docx', addedBy: 'You', date: '4 days ago', size: '3.2 MB' },
      { id: 'd3', name: 'Pricing Model - Draft 3.xlsx', type: 'xlsx', addedBy: 'Kevin', date: '1 week ago', size: '245 KB' },
    ],
    suggestedDrafts: [
      {
        id: 's1', type: 'email', title: 'Pricing Finalization Deadline',
        recipient: 'Kevin',
        reason: '10 days to launch and pricing isn\'t locked',
        preview: 'Kevin, with our March 1 launch approaching, we need to lock pricing by this Friday. Can we schedule 30 min to finalize...',
      },
      {
        id: 's2', type: 'document', title: 'Go/No-Go Checklist',
        reason: 'Launch readiness assessment needed before Feb 25',
        preview: 'A structured checklist covering pricing, documentation, support readiness, and marketing alignment...',
      },
    ],
    openQuestions: [
      { id: 'q1', question: 'What is the final enterprise tier price point?', priority: 'high', source: 'Pricing discussion' },
      { id: 'q2', question: 'Is support team staffed for enterprise SLA?', priority: 'medium', source: 'Launch readiness' },
    ],
  },
  lakeside: {
    summary: 'Lakeside Plastics is a critical churn risk. Their champion left the company, NPS dropped to 4, and the account has been declining for 6 weeks. Immediate executive intervention needed.',
    riskFactors: [
      'Champion left the company — no replacement identified',
      'NPS score dropped from 8 to 4 in one quarter',
      'Usage decreased 40% in the last 6 weeks',
      'Similar pattern to Westfield loss in Q3',
    ],
    nextSteps: [
      'Identify and reach out to new decision maker',
      'Schedule executive business review',
      'Prepare retention offer with value demonstration',
    ],
    documents: [
      { id: 'd1', name: 'Lakeside Account Health Report.pdf', type: 'pdf', addedBy: 'Maya (AI)', date: '2 days ago', size: '420 KB' },
      { id: 'd2', name: 'Usage Decline Analysis.xlsx', type: 'xlsx', addedBy: 'Maya (AI)', date: '1 week ago', size: '180 KB' },
    ],
    suggestedDrafts: [
      {
        id: 's1', type: 'email', title: 'Executive Business Review Request',
        recipient: 'Lakeside Leadership',
        reason: 'Account is at critical churn risk — need executive engagement',
        preview: 'Dear Lakeside team, I\'d like to schedule a brief executive review to discuss how we can better support your team\'s goals...',
      },
      {
        id: 's2', type: 'document', title: 'Retention Value Proposal',
        reason: 'Need to demonstrate ROI to prevent churn',
        preview: 'A tailored value analysis showing Lakeside\'s usage patterns, cost savings achieved, and recommended optimization...',
      },
    ],
    openQuestions: [
      { id: 'q1', question: 'Who is the new decision maker replacing the departed champion?', priority: 'high', source: 'Stakeholder change' },
      { id: 'q2', question: 'What caused the NPS drop — product issues or relationship?', priority: 'high', source: 'NPS analysis' },
      { id: 'q3', question: 'Is Lakeside evaluating competitors?', priority: 'high', source: 'Churn pattern analysis' },
    ],
  },
  cascade: {
    summary: 'Cascade Dynamics has gone dark — no response for 3 weeks despite multiple touchpoints. The $95K deal is stalling. Need to try alternative contacts or channels.',
    riskFactors: [
      '3 weeks of silence across all channels',
      'Last meeting was rescheduled twice before going silent',
    ],
    nextSteps: [
      'Try reaching out via LinkedIn or phone',
      'Ask for introduction through mutual connection',
      'Prepare a "breakup email" to force a response',
    ],
    documents: [
      { id: 'd1', name: 'Cascade Deal Summary.docx', type: 'docx', addedBy: 'You', date: '3 weeks ago', size: '890 KB' },
    ],
    suggestedDrafts: [
      {
        id: 's1', type: 'email', title: 'Breakup Email — Cascade',
        reason: '3 weeks no response — a direct "should we close this out?" often forces a reply',
        preview: 'Hi team, I haven\'t been able to reach you over the past few weeks. I understand priorities shift — should we pause our conversation...',
      },
    ],
    openQuestions: [
      { id: 'q1', question: 'Was there an internal change at Cascade that caused the silence?', priority: 'high', source: 'Communication gap' },
      { id: 'q2', question: 'Is the $95K budget still allocated?', priority: 'medium', source: 'Deal health' },
    ],
  },
  pipeline: {
    summary: 'Pipeline Strategy Q1 needs updating. Sarah Chen reviewed it a week ago but no changes have been made. Two new market opportunities identified that aren\'t reflected in the current pipeline.',
    riskFactors: [
      'Strategy document is outdated — doesn\'t reflect current market',
    ],
    nextSteps: [
      'Schedule strategy review session with Sarah',
      'Incorporate new market opportunities into pipeline model',
    ],
    documents: [
      { id: 'd1', name: 'Q1 Pipeline Strategy.docx', type: 'docx', addedBy: 'Sarah Chen', date: '1 month ago', size: '1.5 MB' },
      { id: 'd2', name: 'Market Opportunity Analysis.xlsx', type: 'xlsx', addedBy: 'Maya (AI)', date: '1 week ago', size: '280 KB' },
    ],
    suggestedDrafts: [],
    openQuestions: [
      { id: 'q1', question: 'What are the two new market opportunities and their estimated value?', priority: 'medium', source: 'Market analysis' },
    ],
  },
  competitive: {
    summary: 'The competitive analysis is 8 months old and significantly outdated. Two new competitors have entered the market since it was written. This is a strategic blind spot.',
    riskFactors: [
      '8 months stale — market has shifted significantly',
      '2 new competitors not analyzed (Forge Systems, NeuralWork)',
    ],
    nextSteps: [
      'Commission updated competitive analysis',
      'Research Forge Systems and NeuralWork positioning',
    ],
    documents: [
      { id: 'd1', name: 'Competitive Landscape Q2-2025.pdf', type: 'pdf', addedBy: 'You', date: '8 months ago', size: '4.2 MB' },
    ],
    suggestedDrafts: [
      {
        id: 's1', type: 'document', title: 'Updated Competitive Brief',
        reason: 'Current analysis is 8 months old with 2 new entrants unaccounted for',
        preview: 'An updated competitive landscape covering Forge Systems (Series B, $30M), NeuralWork (launched Q4), and shifts in existing player positioning...',
      },
    ],
    openQuestions: [
      { id: 'q1', question: 'What is Forge Systems\' pricing model and target market?', priority: 'medium', source: 'New competitor' },
      { id: 'q2', question: 'How does NeuralWork\'s product compare to ours?', priority: 'medium', source: 'New competitor' },
    ],
  },
};

const DEFAULT_INSIGHT: AIInsight = {
  summary: 'No detailed analysis available yet. Click "Open Chat" to discuss this unit with your AI teammate.',
  riskFactors: [],
  nextSteps: [],
  documents: [],
  suggestedDrafts: [],
  openQuestions: [],
};

// ============================================================
// Quick actions
// ============================================================

const QUICK_ACTIONS = [
  { id: 'open-chat', icon: '💬', label: 'Open Chat' },
  { id: 'add-note', icon: '📝', label: 'Add Note' },
  { id: 'create-task', icon: '✅', label: 'Create Task' },
  { id: 'schedule', icon: '📅', label: 'Schedule' },
  { id: 'draft-email', icon: '📧', label: 'Draft Email' },
];

// ============================================================
// Section header
// ============================================================

function SectionHeader({ label, count, action }: { label: string; count?: number; action?: { label: string; onClick: () => void } }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700 }}>
        {label}{count !== undefined ? ` (${count})` : ''}
      </div>
      {action && (
        <button
          onClick={action.onClick}
          style={{
            border: 'none',
            background: 'none',
            fontSize: 11,
            color: C.blue,
            cursor: 'pointer',
            fontWeight: 700,
            padding: '2px 4px',
            fontFamily: 'inherit',
          }}
        >
          + {action.label}
        </button>
      )}
    </div>
  );
}

// ============================================================
// Component
// ============================================================

export default function DetailPanel({ unit, zoneAppearances, onClose, onAction }: DetailPanelProps) {
  const isOpen = unit !== null;
  const health = unit?.health ?? unit?.heat ?? 0;
  const hStatus = useMemo(() => healthLabel(health), [health]);
  const [expandedDraft, setExpandedDraft] = useState<string | null>(null);

  const entityId = unit?.id.split('__')[0] ?? '';
  const insight = AI_INSIGHTS[entityId] ?? DEFAULT_INSIGHT;

  const people = unit?.children.filter(c => c.type === PresenceShape.PERSON) ?? [];
  const items = unit?.children.filter(c => c.type !== PresenceShape.PERSON) ?? [];

  return (
    <div data-scrollable style={{
      width: isOpen ? 420 : 0,
      minWidth: isOpen ? 420 : 0,
      height: '100%',
      background: C.bg,
      borderLeft: isOpen ? `1px solid ${C.border}` : 'none',
      transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, system-ui, sans-serif',
      overflow: 'hidden',
    }}>
      {unit && (
        <>
          {/* Header */}
          <div style={{
            padding: '20px 22px 14px',
            borderBottom: `1px solid ${C.borderLight}`,
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 6,
                background: ZONE_DISPLAY[unit.zone].bg,
                border: `1px solid ${ZONE_DISPLAY[unit.zone].color}30`,
                fontSize: 11,
                fontWeight: 700,
                color: ZONE_DISPLAY[unit.zone].color,
              }}>
                <span>{ZONE_DISPLAY[unit.zone].icon}</span>
                {ZONE_DISPLAY[unit.zone].label}
              </div>
              <button
                onClick={onClose}
                style={{
                  border: `1px solid ${C.border}`,
                  background: C.surface,
                  borderRadius: 8,
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  fontSize: 14,
                  color: C.textMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.text, lineHeight: 1.25 }}>
                  {unit.name}
                </div>
                {unit.facetLabel && (
                  <div style={{ fontSize: 12, color: C.textSec, marginTop: 3, lineHeight: 1.4 }}>
                    {unit.facetLabel}
                  </div>
                )}
              </div>
              {unit.value && (
                <div style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: C.green,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                  {unit.value}
                </div>
              )}
            </div>

            {/* Health + Activity bars */}
            <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
              {[
                { label: 'Health', value: health, color: hStatus.color, text: hStatus.text },
                { label: 'Activity', value: unit.heat, color: C.blue, text: `${Math.round(unit.heat * 100)}%` },
              ].map(bar => (
                <div key={bar.label}>
                  <div style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 3 }}>
                    {bar.label}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 70, height: 5, borderRadius: 3, background: C.borderLight, overflow: 'hidden' }}>
                      <div style={{ width: `${bar.value * 100}%`, height: '100%', borderRadius: 3, background: bar.color }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: bar.color }}>{bar.text}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick actions */}
            <div style={{ display: 'flex', gap: 4, marginTop: 12, flexWrap: 'wrap' }}>
              {QUICK_ACTIONS.map(action => (
                <button
                  key={action.id}
                  onClick={() => onAction?.(action.id, entityId)}
                  style={{
                    border: `1px solid ${C.border}`,
                    background: C.bg,
                    borderRadius: 6,
                    padding: '5px 10px',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.textSec,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 12 }}>{action.icon}</span>
                  {action.label}
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable content */}
          <div data-scrollable style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 22px 22px',
          }}>

            {/* AI Summary */}
            <div style={{ marginBottom: 22 }}>
              <SectionHeader label="AI Summary" />
              <div style={{
                fontSize: 13,
                lineHeight: 1.6,
                color: C.textSec,
                padding: '12px 14px',
                borderRadius: 8,
                background: C.blueBg,
                borderLeft: `3px solid ${C.blue}`,
              }}>
                {insight.summary}
              </div>

              {/* Risk factors */}
              {insight.riskFactors.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {insight.riskFactors.map((risk, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'flex-start',
                      padding: '6px 10px',
                      marginBottom: 3,
                      borderRadius: 6,
                      background: C.redBg,
                      fontSize: 12,
                      color: C.red,
                      lineHeight: 1.45,
                      fontWeight: 500,
                    }}>
                      <span style={{ flexShrink: 0, marginTop: 1 }}>⚠</span>
                      <span>{risk}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Next steps */}
              {insight.nextSteps.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {insight.nextSteps.map((step, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'flex-start',
                      padding: '6px 10px',
                      marginBottom: 3,
                      borderRadius: 6,
                      background: C.greenBg,
                      fontSize: 12,
                      color: C.green,
                      lineHeight: 1.45,
                      fontWeight: 500,
                    }}>
                      <span style={{ flexShrink: 0 }}>→</span>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Open Questions */}
            {insight.openQuestions.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <SectionHeader label="Questions to Answer" count={insight.openQuestions.length} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {insight.openQuestions.map(q => {
                    const ps = priorityStyle[q.priority];
                    return (
                      <div key={q.id} style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: C.bg,
                        border: `1px solid ${C.borderLight}`,
                        borderLeft: `3px solid ${ps.color}`,
                      }}>
                        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.45, fontWeight: 500 }}>
                          {q.question}
                        </div>
                        <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            color: ps.color,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: 9,
                            padding: '2px 6px',
                            borderRadius: 3,
                            background: ps.bg,
                          }}>
                            {q.priority}
                          </span>
                          <span>{q.source}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Suggested Drafts */}
            {insight.suggestedDrafts.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <SectionHeader label="Suggested Drafts" count={insight.suggestedDrafts.length} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {insight.suggestedDrafts.map(draft => {
                    const isExpanded = expandedDraft === draft.id;
                    return (
                      <div key={draft.id} style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: C.bg,
                        border: `1px solid ${C.borderLight}`,
                        cursor: 'pointer',
                      }}
                        onClick={() => setExpandedDraft(isExpanded ? null : draft.id)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 14 }}>{draft.type === 'email' ? '📧' : '📄'}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                              {draft.title}
                            </div>
                            {draft.recipient && (
                              <div style={{ fontSize: 10, color: C.textMuted }}>To: {draft.recipient}</div>
                            )}
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); onAction?.(draft.type === 'email' ? 'generate-email' : 'generate-doc', entityId); }}
                            style={{
                              border: 'none',
                              background: C.blue,
                              borderRadius: 6,
                              padding: '4px 10px',
                              cursor: 'pointer',
                              fontSize: 10,
                              fontWeight: 700,
                              color: '#FFFFFF',
                              fontFamily: 'inherit',
                              whiteSpace: 'nowrap',
                              letterSpacing: 0.3,
                            }}
                          >
                            Generate
                          </button>
                        </div>
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4, lineHeight: 1.35 }}>
                          {draft.reason}
                        </div>
                        {isExpanded && (
                          <div style={{
                            marginTop: 8,
                            padding: '10px 12px',
                            borderRadius: 6,
                            background: C.surface,
                            border: `1px solid ${C.borderLight}`,
                            fontSize: 12,
                            color: C.textSec,
                            lineHeight: 1.55,
                            fontStyle: 'italic',
                          }}>
                            {draft.preview}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Documents */}
            <div style={{ marginBottom: 22 }}>
              <SectionHeader
                label="Documents"
                count={insight.documents.length}
                action={{ label: 'Add', onClick: () => onAction?.('add-document', entityId) }}
              />
              {insight.documents.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {insight.documents.map(doc => (
                    <div key={doc.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      borderRadius: 8,
                      background: C.bg,
                      border: `1px solid ${C.borderLight}`,
                      cursor: 'pointer',
                    }}>
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{docIcon(doc.type)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: C.text,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {doc.name}
                        </div>
                        <div style={{ fontSize: 10, color: C.textMuted }}>
                          {doc.addedBy} · {doc.date}{doc.size ? ` · ${doc.size}` : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: C.textMuted, padding: '8px 0' }}>
                  No documents attached yet
                </div>
              )}
            </div>

            {/* People */}
            {people.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <SectionHeader label="People" count={people.length} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {people.map(person => (
                    <div key={person.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 10px',
                      borderRadius: 8,
                      background: C.bg,
                      border: person.isGhost ? `1px dashed ${C.border}` : `1px solid ${C.borderLight}`,
                    }}>
                      <div style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: person.isGhost
                          ? `repeating-linear-gradient(45deg, ${C.surface}, ${C.surface} 2px, ${C.borderLight} 2px, ${C.borderLight} 4px)`
                          : C.surface,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 700,
                        color: person.isGhost ? C.textFaint : C.textSec,
                        flexShrink: 0,
                      }}>
                        {person.initials || person.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: person.isGhost ? C.textMuted : C.text }}>
                          {person.name}
                        </div>
                        {person.label && (
                          <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                            {person.label}
                          </div>
                        )}
                      </div>
                      {person.isGhost && (
                        <div style={{
                          fontSize: 9,
                          color: C.red,
                          fontWeight: 700,
                          padding: '2px 6px',
                          borderRadius: 3,
                          background: C.redBg,
                          letterSpacing: 0.3,
                          textTransform: 'uppercase',
                        }}>
                          Missing
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related items */}
            {items.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <SectionHeader label="Related Items" count={items.length} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {items.map(item => (
                    <div key={item.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 10px',
                      borderRadius: 8,
                      background: C.bg,
                      border: `1px solid ${C.borderLight}`,
                    }}>
                      <span style={{ fontSize: 14 }}>{getTypeIcon(item.type)}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{item.name}</div>
                        {item.label && <div style={{ fontSize: 10, color: C.textMuted }}>{item.label}</div>}
                      </div>
                      <div style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{getTypeLabel(item.type)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Zone appearances */}
            {zoneAppearances.length > 1 && (
              <div style={{ marginBottom: 22 }}>
                <SectionHeader label={`Appears in ${zoneAppearances.length} zones`} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {zoneAppearances.map(app => {
                    const z = ZONE_DISPLAY[app.zone];
                    const isCurrent = app.zone === unit.zone;
                    return (
                      <div key={app.zone} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: isCurrent ? z.bg : C.bg,
                        border: `1px solid ${isCurrent ? z.color + '30' : C.borderLight}`,
                      }}>
                        <span style={{ fontSize: 13 }}>{z.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: isCurrent ? z.color : C.textSec }}>
                            {z.label}
                            {isCurrent && <span style={{ fontWeight: 400, color: C.textMuted }}> (current)</span>}
                          </div>
                          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 1, lineHeight: 1.3 }}>
                            {app.facetLabel}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
